import React from 'react';
import { ComponentComponentsFormSection } from '../../../types/schema';
import { generatePromptQuestions } from '../../../utils/generatePromptQuestions';
import { FormData } from '../../../utils/mastra';
import { AnswerState, PromptQuestions, QuestionAnswerContext } from '../types';
import { isSectionComplete } from '../utils';
import logging from '../../../utils/logging';

type UseFormWizardProps = {
  filteredSections: (ComponentComponentsFormSection | null | undefined)[];
  isWizardMode: boolean;
  answers: AnswerState;
  addPromptAnswers: (promptAnswers: AnswerState) => void;
  formSlug: string;
  form?: FormData;
  onPromptResultComplete?: () => Promise<string | null>;
};

export const useFormWizard = ({
  filteredSections,
  answers,
  addPromptAnswers,
  form,
}: UseFormWizardProps) => {
  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [promptQuestions, setPromptQuestions] = React.useState<PromptQuestions>({});
  const [loadingPromptSections, setLoadingPromptSections] = React.useState<{
    [sectionId: string]: boolean;
  }>({});

  const [promptSectionErrors, setPromptSectionErrors] = React.useState<{
    [sectionId: string]: string | null;
  }>({});

  const loadPromptQuestions = React.useCallback(
    async (sectionId: string, promptQuestion: string, promptCommon?: string | null) => {
      setLoadingPromptSections((prev) => ({ ...prev, [sectionId]: true }));
      setPromptSectionErrors((prev) => ({ ...prev, [sectionId]: null }));

      try {
        const questionsAndAnswers: QuestionAnswerContext[] = Object.entries(answers)
          .filter(([, answerData]) => answerData.answer !== null)
          .map(([question, answerData]) => ({
            question,
            answer: answerData.answer,
            answerOptions: answerData.answerOptions,
          }));

        const questions = await generatePromptQuestions(
          promptQuestion,
          sectionId,
          questionsAndAnswers,
          form,
          promptCommon,
        );

        setPromptQuestions((prev) => ({
          ...prev,
          [sectionId]: questions,
        }));

        const promptAnswers: AnswerState = {};
        questions.forEach((q, index) => {
          if (!q?.title) return;
          promptAnswers[q.title] = {
            answer: null,
            required: q.required || false,
            error: null,
            index: index + 1,
            hash: q.hash as string,
            answerOptions: q.answerOptions?.map((opt) => ({ title: opt?.title as string })),
            isPrompt: true,
            sectionId: sectionId,
          };
        });

        addPromptAnswers(promptAnswers);
        setLoadingPromptSections((prev) => ({ ...prev, [sectionId]: false }));
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        logging.error('Error loading prompt questions:', err);
        setPromptQuestions((prev) => ({
          ...prev,
          [sectionId]: [],
        }));
        setPromptSectionErrors((prev) => ({
          ...prev,
          [sectionId]: err.message || 'Erro ao carregar perguntas',
        }));
        setLoadingPromptSections((prev) => ({ ...prev, [sectionId]: false }));

        const isCurrentSection = filteredSections[currentSectionIndex]?.id === sectionId;
        if (isCurrentSection && currentSectionIndex < filteredSections.length - 1) {
          setCurrentSectionIndex((prev) => prev + 1);
        }
      }
    },
    [addPromptAnswers, answers, currentSectionIndex, filteredSections],
  );

  const handleAdvanceSection = React.useCallback(() => {
    if (currentSectionIndex < filteredSections.length - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
    }
  }, [currentSectionIndex, filteredSections.length]);

  const allSectionsComplete = React.useMemo(() => {
    return filteredSections.every((section) =>
      isSectionComplete(section, answers, promptQuestions, loadingPromptSections),
    );
  }, [filteredSections, answers, promptQuestions, loadingPromptSections]);

  // Keep current index within visible range when sections appear/disappear
  React.useEffect(() => {
    setCurrentSectionIndex((prev) => {
      const maxIndex = Math.max(filteredSections.length - 1, 0);
      return Math.min(prev, maxIndex);
    });
  }, [filteredSections.length]);

  return {
    currentSectionIndex,
    promptQuestions,
    loadingPromptSections,
    promptSectionErrors,
    isSectionComplete,
    handleAdvanceSection,
    allSectionsComplete,
    loadPromptQuestions,
  };
};
